import * as dayjs from 'dayjs';
import { TimeZone } from '../../../time/timezone.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { DataModel, DataModelBuilder, isSameData, isSameList, SerializationContext, SerializedData } from '../../dto/dto.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { VersionRange } from '../../core/version-range.js';
import { Duration } from 'dayjs/plugin/duration.js';

export enum OpenAccessStatus {
  Open = 'OPEN',
  Scanned = 'SCANNED'
}

export interface OpenAccessScanEntry {
  scannerId: string,
  scanDate: dayjs.Dayjs,
  status: OpenAccessStatus,
}

export class OpenAccessScan extends DataModel {
  static readonly TYPE_ID: string = '59fdaa80bcc745b99d39ad0f6f838894';
  static readonly SCHEMA_VERSION: number = 5;

  public readonly expirationDate: dayjs.Dayjs | null;

  private constructor(
      public readonly businessRef: BusinessModuleRef,
      public readonly eventId: string | null,
      public readonly uniqueId: string,
      public readonly passId: string,
      public readonly passTemplateId: string,
      public readonly scans: OpenAccessScanEntry[],
      public readonly creationDate: dayjs.Dayjs,
      public readonly status: OpenAccessStatus,
      public readonly scanValidity: Duration | null,
  ) {
    super();
    this.expirationDate = scanValidity != null ? creationDate.add(scanValidity) : null;
  }

  get $typeId(): string {
    return OpenAccessScan.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(OpenAccessScan.SCHEMA_VERSION);
  }

  get lastScannerId(): string {
    return this.scans[this.scans.length - 1].scannerId;
  }

  get lastScanDate(): dayjs.Dayjs {
    return this.scans[this.scans.length - 1].scanDate;
  }

  static inside(opts: {
      businessRef: BusinessModuleRef,
      eventId: string | null,
      uniqueId: string,
      passId: string,
      passTemplateId: string,
      scannerId: string,
      scanDate: dayjs.Dayjs,
      creationDate: dayjs.Dayjs,
      scanValidity: Duration | null,
  }) {
    return new OpenAccessScan(
        opts.businessRef,
        opts.eventId,
        opts.uniqueId,
        opts.passId,
        opts.passTemplateId,
        [
          { scannerId: opts.scannerId, scanDate: opts.scanDate, status: OpenAccessStatus.Scanned },
        ],
        opts.creationDate,
        OpenAccessStatus.Scanned,
        opts.scanValidity,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessScan {
    return new OpenAccessScan(
        BusinessModuleRef.mustDecode(MapUtils.get<string>(map, 'businessRef')),
        MapUtils.getOrNull<string>(map, 'eventId'),
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'passId'),
        MapUtils.get<string>(map, 'passTemplateId'),
        MapUtils.getList<OpenAccessScanEntry>(map, 'scans', (value) => ({
          scannerId: MapUtils.get<string>(value as SerializedData, 'scannerId'),
          scanDate: context.decodeDate(MapUtils.get<number>(value as SerializedData, 'scanDate')),
          status: toEnumFromString(MapUtils.get<string>(value as SerializedData, 'status'), OpenAccessStatus),
        })),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, '$status'), OpenAccessStatus),
        context.decodeDurationOrNull(MapUtils.getOrNull<number>(map, 'scanValidity')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'businessRef': this.businessRef.encode(),
      'eventId': this.eventId,
      'uniqueId': this.uniqueId,
      'passId': this.passId,
      'passTemplateId': this.passTemplateId,
      'scans': this.scans.map((e) => ({
        'scannerId': e.scannerId,
        'scanDate': context.encodeDate(e.scanDate),
        'status': fromEnumAsString(e.status),
      })),
      'creationDate': context.encodeDate(this.creationDate),
      '$status': fromEnumAsString(this.status),
      'scanValidity': context.encodeDurationOrNull(this.scanValidity),
    };
  }

  clone(): this {
    return new OpenAccessScan(
        this.businessRef.clone(),
        this.eventId,
        this.uniqueId,
        this.passId,
        this.passTemplateId,
        this.scans.map((e) => ({ ...e })),
        TimeZone.cloneDate(this.creationDate),
        this.status,
        TimeZone.cloneDurationOrNull(this.scanValidity),
    ) as this;
  }

  isSame(other: OpenAccessScan): boolean {
    if(this === other) {
      return true;
    }

    return other.eventId === this.eventId &&
        other.uniqueId === this.uniqueId &&
        other.passId === this.passId &&
        other.passTemplateId === this.passTemplateId &&
        other.creationDate === this.creationDate &&
        other.status === this.status &&
        other.scanValidity == this.scanValidity &&
        isSameData(other.businessRef, this.businessRef) &&
        isSameList(other.scans, this.scans, (a, b) => a.scanDate == b.scanDate && a.scannerId == b.scannerId && a.status == b.status);
  }

  scanIn(opts: { scannerId: string, scanDate: dayjs.Dayjs}): this {
    return new OpenAccessScan(
        this.businessRef.clone(),
        this.eventId,
        this.uniqueId,
        this.passId,
        this.passTemplateId,
        [
          ...this.scans.map((e) => ({ ...e })),
          { scannerId: opts.scannerId, scanDate: opts.scanDate, status: OpenAccessStatus.Scanned },
        ],
        TimeZone.cloneDate(this.creationDate),
        OpenAccessStatus.Scanned,
        TimeZone.cloneDurationOrNull(this.scanValidity),
    ) as this;
  }

  scanOut(opts: { scannerId: string, scanDate: dayjs.Dayjs }): this {
    return new OpenAccessScan(
        this.businessRef.clone(),
        this.eventId,
        this.uniqueId,
        this.passId,
        this.passTemplateId,
        [
          ...this.scans.map((e) => ({ ...e })),
          { scannerId: opts.scannerId, scanDate: opts.scanDate, status: OpenAccessStatus.Open },
        ],
        TimeZone.cloneDate(this.creationDate),
        OpenAccessStatus.Open,
        TimeZone.cloneDurationOrNull(this.scanValidity),
    ) as this;
  }
}

export class OpenAccessScanBuilder extends DataModelBuilder<OpenAccessScan> {
  build(context: SerializationContext, map: SerializedData): OpenAccessScan {
    return OpenAccessScan.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(OpenAccessScan.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OpenAccessScan.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    if(version < 2) {
      const businessRef = context.storage.get<BusinessModuleRef>('businessRef');
      const uniqueId = context.storage.get<string>('uniqueId') ?? MapUtils.getOrNull<string>(map, 'eventId');

      if(businessRef == null || uniqueId == null) {
        return null; // This data is deprecated and we can't migrate it.
      }

      map['businessRef'] = businessRef.encode();
      map['uniqueId'] = uniqueId;

      version = 2;
    }

    // In this version `creationDate` of type `Dayjs` as added.
    if(version < 3) {
      map['creationDate'] = map['scanDate'];
      version = 3;
    }

    // In this version `scanValidity` of type `Duration | null` as added.
    if(version < 4) {
      version = 4;
    }

    // In this version `scans` of type `OpenAccessScanEntry[]` as added.
    if(version < 5) {
      const scannerId = MapUtils.get<string>(map, 'scannerId');
      const scanDate = context.decodeDate(MapUtils.get<number>(map, 'scanDate'));

      map['scans'] = [{
        'scannerId': scannerId,
        'scanDate': context.encodeDate(scanDate),
        'status': fromEnumAsString(OpenAccessStatus.Scanned),
      }];

      version = 5;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
